import React from "react";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { ContextMenu, ContextMenuTrigger } from "@/components/ui/context-menu";
import { CardActionMenu } from "../../UniversalCRMTable/HorizontalCardView/components/CardActionMenu";
import { RowContextMenuContent } from "../../UniversalCRMTable/components/SimpleTable/RowContextMenuContent";
import { renderCellValue } from "../../UniversalCRMTable/utils/cell-value-rendering";
import type {
  BaseEntity,
  SimpleTableConfig,
  EntityHooks,
  FieldValue,
} from "../../UniversalCRMTable/types/shared";
import type { FieldLayout } from "../../UniversalCRMTable/HorizontalCardView/types";

export interface EntityListRowProps<T extends BaseEntity> {
  item: T;
  index: number;
  config: SimpleTableConfig<T>;
  hooks?: EntityHooks<T>;
  /** Primary/secondary title layout — precomputed by the parent so it stays referentially stable. */
  fieldLayout: FieldLayout;
  /** Field ids rendered (muted) in the right cluster — precomputed + stable. */
  rightFieldIds: string[];
  /** Open the detail modal for this row. */
  onOpen?: (item: T, index: number) => void;
  enableSelection?: boolean;
  isSelected?: boolean;
  onSelectChange?: (id: string) => void;
}

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

function readValue<T extends BaseEntity>(item: T, fieldId: string): FieldValue {
  return (item as Record<string, unknown>)[fieldId] as FieldValue;
}

/**
 * EntityListRow — the dense, single-line list row used by every entity in List
 * view. Generic + config-driven: the primary field is the bold title, a couple
 * of muted secondary/badge fields sit at the right, and the full canonical
 * action set is reachable via the `⋯` button AND right-click (ENTITY_TABLE_ROW_ACTIONS).
 * Clicking the row opens the detail modal.
 */
function EntityListRowInner<T extends BaseEntity>({
  item,
  index,
  config,
  hooks,
  fieldLayout,
  rightFieldIds,
  onOpen,
  enableSelection = false,
  isSelected = false,
  onSelectChange,
}: EntityListRowProps<T>) {
  const hasRowActions = Boolean(hooks && (config.detailActions?.length ?? 0) > 0);

  const primaryId = fieldLayout.primaryField;
  const secondaryId = fieldLayout.secondaryField;

  const renderFor = (fieldId: string): React.ReactNode => {
    const fieldConfig = config.fields[fieldId];
    if (!fieldConfig) return null;
    return renderCellValue(readValue(item, fieldId), fieldConfig, fieldId, false, {
      entityRecord: item as Record<string, unknown>,
      entityType: config.entity,
    });
  };

  const handleClick = () => onOpen?.(item, index);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen?.(item, index);
    }
  };

  const row = (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        "group/list-row flex items-center gap-3 px-3 py-2.5 min-w-0 cursor-pointer outline-none",
        "hover:bg-muted/50 focus-visible:bg-muted/50 transition-colors",
        isSelected && "bg-primary/5"
      )}
    >
      {enableSelection && (
        <span onClick={stop} className="shrink-0">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onSelectChange?.(item.id)}
            aria-label="Select row"
          />
        </span>
      )}

      {/* Title (primary) + optional secondary segment */}
      <div className="min-w-0 flex-1 flex items-baseline gap-2">
        {primaryId && (
          <span className="font-medium text-sm text-card-foreground truncate">
            {renderFor(primaryId)}
          </span>
        )}
        {secondaryId && secondaryId !== primaryId && (
          <span className="hidden sm:inline text-xs text-muted-foreground truncate">
            {renderFor(secondaryId)}
          </span>
        )}
      </div>

      {/* Right cluster — muted secondary/badge fields, hidden on narrow widths */}
      {rightFieldIds.length > 0 && (
        <div className="hidden md:flex items-center gap-4 shrink-0 text-xs text-muted-foreground max-w-[55%] overflow-hidden">
          {rightFieldIds.map((fieldId) => (
            <span key={fieldId} className="truncate inline-flex items-center min-w-0">
              {renderFor(fieldId)}
            </span>
          ))}
        </div>
      )}

      {/* Row actions */}
      {hasRowActions && hooks && (
        <div className="shrink-0" onClick={stop}>
          <CardActionMenu item={item} config={config} hooks={hooks} onClick={stop} />
        </div>
      )}
    </div>
  );

  if (hasRowActions && hooks) {
    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>{row}</ContextMenuTrigger>
        <RowContextMenuContent
          item={item}
          actions={config.detailActions ?? []}
          hooks={hooks}
          entityName={config.entity}
          config={config}
        />
      </ContextMenu>
    );
  }

  return row;
}

export const EntityListRow = React.memo(EntityListRowInner) as typeof EntityListRowInner;
